import { BusinessObjectBase } from './base';
import { textBoxSchema } from '@schemas/business-objects/text-box';
import type { Resource } from './resource';
import type { Id } from '../base-types';
import type { User } from './user';

export interface TextBoxData {
  readonly id: Id;
  readonly name?: string;
  readonly fixedPosition?: boolean;
  readonly sequence?: number;
  readonly positionX?: number;
  readonly positionY?: number;
  readonly resourceId: Id;
  readonly resource?: Resource;
  readonly locked?: boolean;
}

export interface TextBox extends Readonly<TextBoxData> {}
export class TextBox extends BusinessObjectBase {
  static readonly entityName = 'TextBox' as const;
  static readonly plural = 'text-boxes';
  static schema = textBoxSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TextBox[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TextBox(data));
  }

  static async findOne(filter?: any): Promise<TextBox | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TextBox(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TextBox> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TextBox(data);
  }

  static async create(data: Omit<TextBoxData, 'id'>): Promise<TextBox> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TextBox(result);
  }

  static async createMany(data: Omit<TextBoxData, 'id'>[]): Promise<TextBox[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TextBox(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TextBoxData>,
  ): Promise<TextBox> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TextBox(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${TextBox.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
